import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Check, Loader2, CreditCard, ArrowLeft, ArrowRight, CheckCircle2, Sparkles, ShieldCheck, Send } from "lucide-react";

function PriceTag({ cents }) {
  return <span className="font-serif text-4xl font-bold text-navy">{(cents / 100).toFixed(0)}€</span>;
}

function PackagePicker({ packages, onPick, contactEmail, setContactEmail, loading }) {
  const [selected, setSelected] = useState(null);
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-3xl text-navy">Choisissez votre formule</h2>
        <p className="mt-1 text-neutral-600">Trois formats au choix pour donner de la visibilité à votre événement auprès de la communauté WineTech.</p>
      </div>
      <div className="grid gap-5 md:grid-cols-3" data-testid="pkg-picker-grid">
        {packages.map((pkg, idx) => {
          const active = selected === pkg.id;
          const highlight = idx === 1;
          return (
            <button
              key={pkg.id}
              type="button"
              onClick={() => setSelected(pkg.id)}
              className={`relative text-left rounded-2xl border-2 bg-white p-6 transition-all ${active ? "border-wine-500 shadow-xl scale-[1.02]" : "border-neutral-200 hover:border-wine-300"} ${highlight ? "ring-2 ring-amber-200" : ""}`}
              data-testid={`pkg-${pkg.id}`}
            >
              {highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-amber-400 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">★ Recommandé</span>}
              <h3 className="font-serif text-xl text-navy">{pkg.title}</h3>
              <p className="mt-1 text-xs text-neutral-500">{pkg.description}</p>
              <div className="mt-4"><PriceTag cents={pkg.price_eur} /><span className="ml-1 text-xs text-neutral-500">HT</span></div>
              <div className="mt-1 text-xs font-semibold text-wine-600">Visibilité {pkg.duration_days} jours</div>
              <ul className="mt-4 space-y-1.5 text-sm text-neutral-700">
                {(pkg.features || []).map((f) => (<li key={f} className="flex gap-1.5"><Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-500" /><span>{f}</span></li>))}
              </ul>
              {active && <div className="absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-full bg-wine-500 text-white"><Check className="h-4 w-4" /></div>}
            </button>
          );
        })}
      </div>

      <div className="rounded-2xl border border-wine-100 bg-wine-50/40 p-5">
        <label className="text-[10px] font-bold uppercase text-neutral-500">Votre email</label>
        <input type="email" required value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="vous@example.com" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="pkg-email" />
        <p className="mt-1 text-[11px] text-neutral-500">La facture sera envoyée à cette adresse et c&apos;est ici que vous recevrez le lien d&apos;accès au formulaire après paiement.</p>
        <button
          type="button"
          disabled={!selected || !contactEmail.includes("@") || loading}
          onClick={() => onPick(selected)}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-wine-500 px-6 py-3 text-sm font-bold text-white shadow-lg hover:bg-wine-600 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="pkg-checkout-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Payer et passer au formulaire
        </button>
      </div>
    </div>
  );
}

function EventForm({ onSubmit, submitting, paid, packageTitle }) {
  const [form, setForm] = useState({
    title: "", description: "", start_date: "", end_date: "", location_city: "", location_country: "France",
    venue: "", online: false, image_url: "", registration_url: "", organizer_name: "", organizer_company: "",
    contact_email: "", contact_phone: "", category: "general", tags: [],
  });
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim() || !form.start_date || !form.organizer_name.trim() || !form.contact_email.trim()) {
      return toast.error("Merci de remplir les champs obligatoires (*)");
    }
    onSubmit(form);
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4 text-sm" data-testid="form-status-banner">
        {paid ? (
          <>✅ Paiement <strong>« {packageTitle} »</strong> confirmé. Remplissez le formulaire ci-dessous pour soumettre votre événement à modération.</>
        ) : (
          <>🟢 Vous êtes connecté·e en tant que membre — soumission <strong>gratuite</strong>. Votre événement passera ensuite par notre modération avant publication.</>
        )}
      </div>

      <h2 className="font-serif text-2xl text-navy">Détails de l&apos;événement</h2>

      <div>
        <label className="text-[10px] font-bold uppercase text-neutral-500">Titre *</label>
        <input required value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Ex. Wine Tech Innovation Summit 2026" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="form-title" />
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase text-neutral-500">Description complète *</label>
        <textarea required rows={6} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Décrivez l'événement, son programme, son public, ses objectifs..." className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="form-description" />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-[10px] font-bold uppercase text-neutral-500">Date de début *</label>
          <input type="date" required value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="form-start-date" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-neutral-500">Date de fin (si plusieurs jours)</label>
          <input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="form-end-date" />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-[10px] font-bold uppercase text-neutral-500">Ville *</label>
          <input required value={form.location_city} onChange={(e) => set("location_city", e.target.value)} placeholder="Bordeaux" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="form-city" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-neutral-500">Pays *</label>
          <input required value={form.location_country} onChange={(e) => set("location_country", e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="form-country" />
        </div>
      </div>

      <div>
        <label className="text-[10px] font-bold uppercase text-neutral-500">Lieu / Venue</label>
        <input value={form.venue} onChange={(e) => set("venue", e.target.value)} placeholder="Palais des Congrès, Cité du Vin..." className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="form-venue" />
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.online} onChange={(e) => set("online", e.target.checked)} className="accent-wine-500" data-testid="form-online" />
        <span>Événement en ligne (visioconférence)</span>
      </label>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-[10px] font-bold uppercase text-neutral-500">Catégorie</label>
          <select value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="form-category">
            <option value="general">Général</option>
            <option value="salon">Salon / Foire</option>
            <option value="conference">Conférence</option>
            <option value="workshop">Atelier / Workshop</option>
            <option value="networking">Networking</option>
            <option value="formation">Formation</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-neutral-500">Image (URL)</label>
          <input type="url" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://..." className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="form-image-url" />
        </div>
      </div>

      <div>
        <label className="text-[10px] font-bold uppercase text-neutral-500">Lien d&apos;inscription (URL)</label>
        <input type="url" value={form.registration_url} onChange={(e) => set("registration_url", e.target.value)} placeholder="https://billetterie.example.com/..." className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="form-reg-url" />
      </div>

      <hr className="border-neutral-200" />

      <h2 className="font-serif text-2xl text-navy">Organisateur</h2>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-[10px] font-bold uppercase text-neutral-500">Nom complet *</label>
          <input required value={form.organizer_name} onChange={(e) => set("organizer_name", e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="form-org-name" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-neutral-500">Société / Organisation</label>
          <input value={form.organizer_company} onChange={(e) => set("organizer_company", e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="form-org-company" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-neutral-500">Email de contact *</label>
          <input required type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="form-contact-email" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-neutral-500">Téléphone</label>
          <input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="form-contact-phone" />
        </div>
      </div>

      <div className="flex justify-end pt-3">
        <button type="submit" disabled={submitting} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-7 py-3 text-sm font-bold text-white shadow-lg hover:bg-wine-600 disabled:opacity-50" data-testid="form-submit-btn">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Soumettre à modération
        </button>
      </div>
    </form>
  );
}

export default function EventSubmitPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();

  const [packages, setPackages] = useState([]);
  const [contactEmail, setContactEmail] = useState("");
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [paidContext, setPaidContext] = useState(null); // {paid, filled, package_title}
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Load packages for non-members
  useEffect(() => {
    if (!user) api.get("/public/event-packages").then((r) => setPackages(r.data || [])).catch(() => {});
  }, [user]);

  // If we have a token, verify it (Stripe payment check)
  useEffect(() => {
    if (!token) return;
    setVerifying(true);
    api.get(`/public/event-submission/${token}`)
      .then((r) => setPaidContext(r.data))
      .catch(() => setPaidContext({ paid: false, filled: false, error: true }))
      .finally(() => setVerifying(false));
  }, [token]);

  const startCheckout = async (pkgId) => {
    setLoadingCheckout(true);
    try {
      const r = await api.post("/public/event-submission/start", { package_id: pkgId, contact_email: contactEmail });
      if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur paiement"); }
    finally { setLoadingCheckout(false); }
  };

  const submitForm = async (form) => {
    setSubmitting(true);
    try {
      if (token) await api.post(`/public/event-submission/${token}`, form);
      else await api.post("/events/submit", form);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setSubmitting(false); }
  };

  // -------- RENDER --------
  return (
    <PublicLayout>
      <section className="mx-auto max-w-4xl px-4 py-12 md:py-16">
        <Link to="/events" className="inline-flex items-center gap-1.5 text-sm font-semibold text-wine-700 hover:underline mb-4"><ArrowLeft className="h-4 w-4" /> Retour aux événements</Link>

        <div className="rounded-3xl bg-gradient-to-br from-wine-500 to-wine-700 p-8 text-white shadow-2xl mb-8" data-testid="submit-event-hero">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-bold uppercase tracking-wider"><Calendar className="h-3.5 w-3.5" /> Publier un événement</div>
          <h1 className="mt-3 font-serif text-3xl md:text-4xl">Donnez de la visibilité à votre événement WineTech</h1>
          <p className="mt-3 text-white/90">Salons, conférences, ateliers, networking — atteignez la communauté tech &amp; vin la plus engagée de France.</p>
        </div>

        {success && (
          <div className="rounded-3xl border-2 border-emerald-200 bg-emerald-50/40 p-10 text-center" data-testid="submit-success">
            <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-500" />
            <h2 className="mt-4 font-serif text-3xl text-navy">Soumission reçue !</h2>
            <p className="mt-2 text-neutral-700">Votre événement est en cours de modération. Vous recevrez un email dès qu&apos;il sera approuvé et publié sur La WineTech.</p>
            <Link to="/events" className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-6 py-2.5 text-sm font-bold text-white hover:bg-wine-600"><ArrowLeft className="h-4 w-4" /> Voir tous les événements</Link>
          </div>
        )}

        {!success && verifying && (
          <div className="rounded-2xl border border-neutral-100 bg-white p-10 text-center" data-testid="verifying-payment"><Loader2 className="mx-auto h-8 w-8 animate-spin text-wine-500" /><p className="mt-3 text-sm text-neutral-500">Vérification du paiement…</p></div>
        )}

        {/* Member flow (logged-in) */}
        {!success && !verifying && user && !token && (
          <div className="rounded-2xl border border-neutral-100 bg-white p-6 md:p-8 shadow-md">
            <EventForm onSubmit={submitForm} submitting={submitting} paid={false} />
          </div>
        )}

        {/* Paid public flow — Step 1: package picker */}
        {!success && !verifying && !user && !token && (
          <div className="rounded-2xl border border-neutral-100 bg-white p-6 md:p-8 shadow-md">
            <div className="mb-6 flex items-start justify-between gap-3 rounded-xl bg-amber-50 border border-amber-200 p-4">
              <div>
                <p className="text-sm font-bold text-amber-900">🟢 Vous êtes membre La WineTech ?</p>
                <p className="text-xs text-amber-700">Connectez-vous pour publier <strong>gratuitement</strong> votre événement.</p>
              </div>
              <Link to={`/login?next=${encodeURIComponent("/evenements/publier")}`} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-2 text-xs font-bold text-white hover:bg-wine-600 whitespace-nowrap" data-testid="login-member-cta">
                Se connecter <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <PackagePicker packages={packages} onPick={startCheckout} contactEmail={contactEmail} setContactEmail={setContactEmail} loading={loadingCheckout} />

            <div className="mt-6 grid gap-3 sm:grid-cols-3 text-center text-xs">
              <div className="rounded-xl bg-neutral-50 p-3"><Sparkles className="mx-auto mb-1 h-4 w-4 text-wine-500" /><strong className="block text-navy">Visibilité ciblée</strong>Communauté WineTech française</div>
              <div className="rounded-xl bg-neutral-50 p-3"><ShieldCheck className="mx-auto mb-1 h-4 w-4 text-emerald-500" /><strong className="block text-navy">Modération qualité</strong>Tous les événements vérifiés</div>
              <div className="rounded-xl bg-neutral-50 p-3"><CreditCard className="mx-auto mb-1 h-4 w-4 text-wine-500" /><strong className="block text-navy">Paiement sécurisé</strong>Stripe — facture envoyée par email</div>
            </div>
          </div>
        )}

        {/* Paid public flow — Step 2: paid token + form */}
        {!success && !verifying && token && paidContext && paidContext.paid && !paidContext.filled && (
          <div className="rounded-2xl border border-neutral-100 bg-white p-6 md:p-8 shadow-md">
            <EventForm onSubmit={submitForm} submitting={submitting} paid={true} packageTitle={paidContext.package_title} />
          </div>
        )}

        {/* Token but already filled */}
        {!success && !verifying && token && paidContext && paidContext.filled && (
          <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-10 text-center">
            <h2 className="font-serif text-2xl text-navy">Formulaire déjà soumis</h2>
            <p className="mt-2 text-neutral-700">Ce lien a déjà été utilisé. Si vous souhaitez modifier votre soumission, contactez-nous à <a href="mailto:contact@lawinetech.com" className="font-bold text-wine-700">contact@lawinetech.com</a>.</p>
          </div>
        )}

        {/* Token but payment not confirmed yet */}
        {!success && !verifying && token && paidContext && !paidContext.paid && (
          <div className="rounded-2xl border-2 border-rose-200 bg-rose-50/40 p-10 text-center">
            <h2 className="font-serif text-2xl text-navy">Paiement non confirmé</h2>
            <p className="mt-2 text-neutral-700">Nous n&apos;avons pas encore reçu confirmation de votre paiement. Patientez quelques secondes et rechargez la page, ou contactez-nous à <a href="mailto:contact@lawinetech.com" className="font-bold text-wine-700">contact@lawinetech.com</a> si le problème persiste.</p>
            <button onClick={() => window.location.reload()} className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600">Rafraîchir</button>
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
